import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getAccessToken } from '../../../services/authService';
import { getApiBaseUrl } from '../../../config/appConfig';
import EtlJobHistory from './EtlJobHistory';
import './EtlImport.scss';

const VALID_PREFIXES = ['ESAI_Data_', 'ESAI_MetaData_'];

function validateFileName(name) {
  if (!name.endsWith('.csv')) return 'File must be a .csv file';
  if (!VALID_PREFIXES.some(p => name.startsWith(p))) {
    return 'File name must start with ESAI_Data_ or ESAI_MetaData_';
  }
  return null;
}

export default function EtlImport() {
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [uploadState, setUploadState] = useState('idle'); // idle | uploading | polling | completed | partial | timed_out | failed
  const [activeJob, setActiveJob] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const token = await getAccessToken(instance, accounts[0]);
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/etlJobStatus?jobId=${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch job status');
      const job = await res.json();
      setActiveJob(job);

      if (job.status === 'completed' || job.status === 'partial') {
        setUploadState(job.status);
        stopPolling();
        setRefreshKey(k => k + 1);
      } else if (job.status === 'failed' || job.status === 'timed_out') {
        setUploadState(job.status);
        setError(job.errorMessage || (job.status === 'timed_out' ? 'Job timed out' : 'Job failed'));
        stopPolling();
        setRefreshKey(k => k + 1);
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [instance, accounts, stopPolling]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    setUploadState('polling');
    pollJobStatus(jobId); // immediate first poll
    pollIntervalRef.current = setInterval(() => pollJobStatus(jobId), 3000);
  }, [stopPolling, pollJobStatus]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validateFileName(file.name);
    if (err) {
      setValidationError(err);
      setSelectedFile(null);
    } else {
      setValidationError(null);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Abandon any in-flight polling so this upload takes over the active-job card.
    // The previous job continues server-side and will appear in Job History when it finishes.
    stopPolling();
    setUploadState('uploading');
    setError(null);
    setActiveJob(null);
    setRefreshKey(k => k + 1);

    try {
      const token = await getAccessToken(instance, accounts[0]);
      const apiBase = getApiBaseUrl();

      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const res = await fetch(`${apiBase}/etlUpload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          file: base64
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Upload failed (${res.status})`);
      }

      const { jobId, blobName, originalFileName } = await res.json();
      setActiveJob({
        id: jobId,
        status: 'queued',
        blobName: blobName || selectedFile.name,
        originalFileName: originalFileName || selectedFile.name
      });
      startPolling(jobId);

      // Reset file input
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadState('failed');
      setError(err.message);
    }
  };

  const handleReset = () => {
    stopPolling();
    setUploadState('idle');
    setActiveJob(null);
    setError(null);
    setSelectedFile(null);
    setValidationError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadLog = async (logBlobName) => {
    try {
      const token = await getAccessToken(instance, accounts[0]);
      const apiBase = getApiBaseUrl();
      const res = await fetch(
        `${apiBase}/etlLogDownload?blobName=${encodeURIComponent(logBlobName)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to get log download URL');
      const { url } = await res.json();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Log download error:', err);
    }
  };

  const handleDownloadFile = async (blobName) => {
    try {
      const token = await getAccessToken(instance, accounts[0]);
      const apiBase = getApiBaseUrl();
      const res = await fetch(
        `${apiBase}/etlFileDownload?blobName=${encodeURIComponent(blobName)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to get file download URL');
      const { url } = await res.json();
      window.open(url, '_blank');
    } catch (err) {
      console.error('File download error:', err);
    }
  };

  const progressPercent = activeJob?.totalRows
    ? Math.round((activeJob.processedRows / activeJob.totalRows) * 100)
    : null;

  return (
    <div className="etl-import">
      <h3>Data Import</h3>

      {/* Upload Section */}
      <div className="etl-upload-section">
        <div className="etl-file-picker">
          <label htmlFor="etl-file-input">Select CSV file to import:</label>
          <input
            ref={fileInputRef}
            id="etl-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            disabled={uploadState === 'uploading'}
          />
          {validationError && (
            <div className="etl-validation-error">{validationError}</div>
          )}
          <div className="etl-file-hint">
            File name must start with <code>ESAI_Data_</code> or <code>ESAI_MetaData_</code>
          </div>
        </div>

        <button
          className="etl-upload-btn"
          onClick={handleUpload}
          disabled={!selectedFile || uploadState === 'uploading'}
        >
          {uploadState === 'uploading' ? 'Uploading...' : 'Upload & Import'}
        </button>
      </div>

      {/* Active Job Status */}
      {activeJob && uploadState !== 'idle' && (
        <div className={`etl-job-status etl-job-status--${activeJob.status}`}>
          <div className="etl-job-status-header">
            <strong>{activeJob.originalFileName || (activeJob.blobName || '').split('/').pop() || activeJob.blobName}</strong>
            <span className={`etl-status-badge etl-status-badge--${activeJob.status}`}>
              {activeJob.status}
            </span>
          </div>

          {(activeJob.status === 'running' || activeJob.status === 'completed' || activeJob.status === 'partial') && activeJob.totalRows && (
            <div className="etl-progress">
              <div className="etl-progress-bar">
                <div
                  className="etl-progress-fill"
                  style={{ width: `${progressPercent || 0}%` }}
                />
              </div>
              <div className="etl-progress-details">
                <span>{activeJob.processedRows?.toLocaleString()} / {activeJob.totalRows?.toLocaleString()} rows</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="etl-progress-counts">
                <span className="etl-count-success">Success: {activeJob.successCount?.toLocaleString()}</span>
                <span className="etl-count-failure">Failed: {activeJob.failureCount?.toLocaleString()}</span>
              </div>
            </div>
          )}

          {activeJob.status === 'queued' && (
            <div className="etl-progress-details">Waiting for job to start...</div>
          )}

          {(activeJob.status === 'failed' || activeJob.status === 'timed_out') && error && (
            <div className="etl-error-message">{error}</div>
          )}

          {activeJob.logBlobName && (
            <button
              className="etl-download-log-btn"
              onClick={() => handleDownloadLog(activeJob.logBlobName)}
              style={{ marginTop: '0.75rem' }}
            >
              Download Error Log
            </button>
          )}

          {(activeJob.status === 'completed' || activeJob.status === 'partial' || activeJob.status === 'timed_out' || activeJob.status === 'failed') ? (
            <button className="etl-reset-btn" onClick={handleReset}>
              Import Another File
            </button>
          ) : (
            <button className="etl-reset-btn" onClick={handleReset}>
              Dismiss (job continues in background)
            </button>
          )}
        </div>
      )}

      {error && uploadState === 'failed' && !activeJob && (
        <div className="etl-error-message">
          {error}
          <button className="etl-reset-btn" onClick={handleReset}>Try Again</button>
        </div>
      )}

      {/* Job History */}
      <EtlJobHistory
        refreshKey={refreshKey}
        onResumePolling={(jobId) => startPolling(jobId)}
        onDownloadLog={handleDownloadLog}
        onDownloadFile={handleDownloadFile}
      />
    </div>
  );
}
